/**
 * Images as a first-class data item (backend: internal/twin/images.go, migration 0074).
 *
 * An image tag (data_kind='image') is added to equipment like any datapoint; a captured
 * photo becomes an image reading whose bytes live inline in Postgres. This module lists a
 * thing's captured images (latest + history for the image widget), fetches one image's
 * bytes as an authenticated blob (→ object URL → <img>, since a raw <img src> can't send
 * the auth header), and uploads a new photo.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** One captured image (the datapoint; bytes fetched separately via blob). */
export interface ImageDatapoint {
  image_id: string;
  metric: string;
  content_type: string;
  byte_size: number;
  width: number;
  height: number;
  compressed: boolean;
  subject_kind?: string;
  subject_id?: string;
  captured_at: string;
}

export interface UploadImageInput {
  file: Blob;
  /** The image tag (metric) on the thing to attach this photo to. */
  metric: string;
  /** Whether the app already compressed the bytes (per the tag's compress option). */
  compressed?: boolean;
  width?: number;
  height?: number;
  /** Optional NDT part link — ties the photo to a tracked unit / QC record. */
  subjectKind?: string;
  subjectId?: string;
  /** Filename hint for the multipart part. */
  filename?: string;
}

export const images = {
  /** A thing's captured images (optionally one image tag), newest first. */
  list: (thingId: string, opts?: { metric?: string; limit?: number }) => {
    const qp = new URLSearchParams();
    if (opts?.metric) qp.set("metric", opts.metric);
    if (opts?.limit) qp.set("limit", String(opts.limit));
    const suffix = qp.toString() ? `?${qp.toString()}` : "";
    return api.get<Paginated<ImageDatapoint>>(
      `/v1/twin/things/${thingId}/images${suffix}`,
    );
  },

  /** Images tied to a subject (a tracked unit / QC record) — part-level NDT gallery. */
  listBySubject: (subjectId: string, opts?: { subjectKind?: string; limit?: number }) => {
    const qp = new URLSearchParams({ subject_id: subjectId });
    if (opts?.subjectKind) qp.set("subject_kind", opts.subjectKind);
    if (opts?.limit) qp.set("limit", String(opts.limit));
    return api.get<Paginated<ImageDatapoint>>(`/v1/twin/images?${qp.toString()}`);
  },

  /** Fetch one image's bytes as an authenticated Blob (caller makes the object URL). */
  blob: (imageId: string) => api.getBlob(`/v1/twin/images/${imageId}`),

  /** Build a stable object-URL fetch for an image; caller revokes when done. */
  objectUrl: async (imageId: string): Promise<string> => {
    const blob = await api.getBlob(`/v1/twin/images/${imageId}`);
    return URL.createObjectURL(blob);
  },

  /** Upload a photo against an image tag on the thing (multipart). */
  upload: (thingId: string, input: UploadImageInput) => {
    const fd = new FormData();
    fd.append("file", input.file, input.filename ?? "photo");
    fd.append("metric", input.metric);
    fd.append("compressed", input.compressed ? "true" : "false");
    if (input.width) fd.append("width", String(input.width));
    if (input.height) fd.append("height", String(input.height));
    if (input.subjectKind) fd.append("subject_kind", input.subjectKind);
    if (input.subjectId) fd.append("subject_id", input.subjectId);
    return api.postForm<ImageDatapoint>(`/v1/things/${thingId}/images`, fd);
  },
};
